// src/modules/care-gaps/pages/CareGapDetailDrawer.jsx
import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import {
  Drawer,
  Box,
  Stack,
  Typography,
  Chip,
  Button,
  Tabs,
  Tab,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from "@mui/material";

// Icons
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AssignmentIcon from "@mui/icons-material/Assignment";
import DescriptionIcon from "@mui/icons-material/Description";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import LinkIcon from "@mui/icons-material/Link";

// Contexts
import { InfoPat2Context } from "../../../context/InfoPat2Context/InfoPat2Context";
import { NoRecentVisitContext } from "../../../context/PatientContext/NoRecentVisitContext/NoRecentVisitContext";
import { AdvancedMdContext } from "../../../context/AdvancedMD/AdvancedMdContext";

import { formatDate } from "../common/helpers.js";
import { getPatientInfo } from "../../../utils/services/Patients/getPatientInfo";

// ===================== Helpers =====================
const safeText = (v, fallback = "—") =>
  v === null || v === undefined || v === "" ? fallback : String(v);

function formatDateShort(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return String(dateStr);
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

function shortAddress(candidate) {
  if (!candidate) return "—";
  const a1 = candidate.address1 || candidate.addressLine1 || "";
  const a2 = candidate.address2 || candidate.addressLine2 || "";
  const out = `${a1}${a1 && a2 ? " " : ""}${a2}`.trim();
  return out || "—";
}

function InfoRow({ icon, label, value }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box sx={{ width: 22, display: "flex", justifyContent: "center", opacity: 0.9 }}>
        {icon}
      </Box>
      <Typography variant="body2" sx={{ minWidth: 100, color: "text.secondary" }}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} sx={{ wordBreak: "break-word" }}>
        {safeText(value)}
      </Typography>
    </Stack>
  );
}

// Helper para extraer texto plano de una nota clínica
function flattenNoteToText(note) {
  if (!note) return "";
  const id = note.noteId ?? note.id ?? "";
  const dt = note.noteDateTime ?? note.noteDate ?? note.date ?? "";
  const template = note.templateName ?? note.__group ?? "";
  const provider = note.userName ?? note.providerName ?? "";
  const signed = note.signedByUser ?? "";
  const reviewed = note.reviewedByUser ?? "";

  const fields = note.fields && typeof note.fields === "object" ? note.fields : {};
  const fieldsText = Object.entries(fields)
    .map(([k, v]) => `${k}: ${String(v ?? "")}`)
    .join("\n");

  return (
    `--- NOTE ---\n` +
    `ID: ${id}\n` +
    `Date: ${dt}\n` +
    `Template: ${template}\n` +
    `Provider: ${provider}\n` +
    `Signed by: ${signed}\n` +
    `Reviewed by: ${reviewed}\n` +
    `\nFields:\n${fieldsText}`
  );
}

// Tipos de notas clínicas
const NOTE_TYPES = [
  { key: "followUpExam", label: "Follow Up Exam 2.0", color: "primary" },
  { key: "annualWellness", label: "Annual Wellness 2.0", color: "success" },
  { key: "telehealth", label: "TELEHEALTH_2.0", color: "info" },
  { key: "hospitalDischarge", label: "Hospital Discharge Follow Up", color: "warning" },
];

// ===================== TabPanel =====================
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`caregap-tabpanel-${index}`}
      aria-labelledby={`caregap-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

// ===================== Main Component =====================
export default function CareGapDetailDrawer({
  open,
  onClose,
  item,
  onSchedule,
  onSubmitEvidence,
  onLogOutreach,
  onCareGapActions,
  onShowObservations,
}) {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedNote, setSelectedNote] = useState(null);
  const [notesLoaded, setNotesLoaded] = useState(false);

  // Contexts
  const {
    membership,
    membershipLoading,
    membershipError,
    loadPatientMembership,
  } = useContext(InfoPat2Context);

  const {
    fetchNoRecentVisitDetails,
    NoRecentVisitDetails,
    detailsLoading: lastVisitLoading,
    detailsError: lastVisitError,
  } = useContext(NoRecentVisitContext);

  const {
    matchPatientInAdvancedMd,
    patientLookupResults,
    lookupLoading,
    lookupError,

    followUpExamNotes,
    annualWellnessNotes,
    telehealthNotes,
    hospitalDischargeNotes,

    followUpExamLoading,
    annualWellnessLoading,
    telehealthLoading,
    hospitalDischargeLoading,

    loadFollowUpExamNotes,
    loadAnnualWellnessNotes,
    loadTelehealthNotes,
    loadHospitalDischargeNotes,
  } = useContext(AdvancedMdContext);

  const cipId = item?.cipId;
  const patientName = item?.patientFullName || item?.patientName || "";

  // State for tracking the AdvancedMD patient ID
  const [advancedMdPatientId, setAdvancedMdPatientId] = useState(null);
  const [notesLoadedForId, setNotesLoadedForId] = useState(null);
  const [matchAttempted, setMatchAttempted] = useState(false);

  // State for patient info from API
  const [patientInfo, setPatientInfo] = useState(null);
  const [patientInfoLoading, setPatientInfoLoading] = useState(false);
  const [patientInfoError, setPatientInfoError] = useState(null);

  // Extract candidate from lookup results (same logic as PatientMapPage)
  const bestMatch = useMemo(() => {
    if (patientLookupResults && patientLookupResults.length > 0) {
      return patientLookupResults[0];
    }
    return null;
  }, [patientLookupResults]);

  const candidate = bestMatch?.candidate ?? bestMatch ?? null;

  // Extract chart value from candidate
  const chartValue = useMemo(() => {
    if (!candidate) return null;
    return (
      candidate.chart ||
      candidate.Chart ||
      candidate.chartId ||
      candidate.ChartId ||
      candidate.ChartID ||
      candidate.chart_id ||
      null
    );
  }, [candidate]);

  // Calculate match confidence
  const confidence = useMemo(() => {
    if (!bestMatch) return null;
    const rawScore =
      bestMatch.score ??
      bestMatch.matchScore ??
      bestMatch.MatchScore ??
      bestMatch.probability ??
      bestMatch.Probability ??
      (candidate && candidate.score) ??
      null;

    if (rawScore === null || rawScore === undefined || rawScore === "") return null;
    const n = Number(rawScore);
    if (Number.isNaN(n)) return null;
    return n <= 1 ? Math.round(n * 100) : Math.round(n);
  }, [bestMatch, candidate]);

  // AdvancedMD status
  const advStatus = useMemo(() => {
    if (lookupLoading) return { label: "Matching…", color: "default" };
    if (lookupError) return { label: "Error", color: "error" };
    if (candidate) return { label: "Matched", color: "success" };
    if (matchAttempted) return { label: "No match", color: "warning" };
    return { label: "Not searched", color: "default" };
  }, [lookupLoading, lookupError, candidate, matchAttempted]);

  // Reset tab and state when drawer closes or item changes
  useEffect(() => {
    if (!open) {
      setActiveTab(0);
      setNotesLoaded(false);
      setAdvancedMdPatientId(null);
      setNotesLoadedForId(null);
      setMatchAttempted(false);
      setPatientInfo(null);
      setPatientInfoError(null);
    }
  }, [open]);

  // Load membership and last visit when item changes
  useEffect(() => {
    if (open && cipId) {
      loadPatientMembership(cipId);
      fetchNoRecentVisitDetails(cipId);
    }
  }, [open, cipId, loadPatientMembership, fetchNoRecentVisitDetails]);

  // Load patient info when drawer opens (for AdvancedMD match)
  useEffect(() => {
    if (!open || !cipId) return;

    const fetchPatientInfo = async () => {
      setPatientInfoLoading(true);
      setPatientInfoError(null);
      try {
        const data = await getPatientInfo(cipId);
        console.log("[CareGapDetailDrawer] Patient info loaded:", data);
        // API returns an array, get first element
        const patient = Array.isArray(data) ? data[0] : data;
        setPatientInfo(patient);
      } catch (err) {
        console.error("[CareGapDetailDrawer] Error loading patient info:", err);
        setPatientInfoError(err.message || "Error loading patient info");
      } finally {
        setPatientInfoLoading(false);
      }
    };

    fetchPatientInfo();
  }, [open, cipId]);

  // Function to perform AdvancedMD match
  const performAdvancedMdMatch = useCallback(async () => {
    if (!patientInfo && !patientName) return;

    // Use patientInfo if available, otherwise parse from patientName
    let firstName, lastName, dob, sex;

    if (patientInfo) {
      firstName = patientInfo.firstName || "";
      lastName = patientInfo.lastName || "";
      // Format dob as YYYY-MM-DD for the match
      dob = patientInfo.dateOfBirth
        ? patientInfo.dateOfBirth.split("T")[0]
        : null;
      sex = patientInfo.gender || "";
    } else {
      // Fallback: Parse patient name
      const nameParts = patientName.split(" ");
      firstName = nameParts[0] || "";
      lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : "";
      dob = null;
      sex = "";
    }

    console.log("[CareGapDetailDrawer] Performing AdvancedMD match:", {
      firstName,
      lastName,
      dob,
      sex,
      patientInfo,
    });

    setMatchAttempted(true);

    // Match patient in AdvancedMD
    await matchPatientInAdvancedMd({
      firstName,
      lastName,
      dob,
      sex,
    });
  }, [patientInfo, patientName, matchPatientInAdvancedMd]);

  // Auto-trigger AdvancedMD match when patientInfo is loaded
  useEffect(() => {
    if (!open || !patientInfo || matchAttempted) return;

    console.log("[CareGapDetailDrawer] Auto-triggering AdvancedMD match...");
    performAdvancedMdMatch();
  }, [open, patientInfo, matchAttempted, performAdvancedMdMatch]);

  // Load clinical notes when tab 2 is selected
  const loadClinicalNotes = useCallback(async () => {
    if (notesLoaded) return;
    await performAdvancedMdMatch();
    setNotesLoaded(true);
  }, [notesLoaded, performAdvancedMdMatch]);

  // When candidate is found, extract and normalize the ID (same logic as PatientMapPage)
  useEffect(() => {
    if (!candidate?.id) {
      console.log("[CareGapDetailDrawer] No candidate ID found. Candidate:", candidate);
      return;
    }

    // Normalize ID: remove "pat" prefix if present
    const rawId = String(candidate.id);
    const normalizedId = rawId.startsWith("pat") ? rawId.slice(3) : rawId;

    console.log("[CareGapDetailDrawer] AdvancedMD candidate found:", {
      rawId,
      normalizedId,
      candidate,
      bestMatch,
    });

    setAdvancedMdPatientId(normalizedId);
  }, [candidate, bestMatch]);

  // When AdvancedMD patient ID is set, load notes
  useEffect(() => {
    if (!advancedMdPatientId) return;
    if (activeTab !== 2) return;
    if (notesLoadedForId === advancedMdPatientId) return;

    console.log("[CareGapDetailDrawer] Loading clinical notes for ID:", advancedMdPatientId);

    loadFollowUpExamNotes(advancedMdPatientId);
    loadAnnualWellnessNotes(advancedMdPatientId);
    loadTelehealthNotes(advancedMdPatientId);
    loadHospitalDischargeNotes(advancedMdPatientId);

    setNotesLoadedForId(advancedMdPatientId);
  }, [
    advancedMdPatientId,
    activeTab,
    notesLoadedForId,
    loadFollowUpExamNotes,
    loadAnnualWellnessNotes,
    loadTelehealthNotes,
    loadHospitalDischargeNotes,
  ]);

  // Debug: Log notes when they change
  useEffect(() => {
    console.log("[CareGapDetailDrawer] Notes state:", {
      followUpExamNotes: followUpExamNotes?.length || 0,
      annualWellnessNotes: annualWellnessNotes?.length || 0,
      telehealthNotes: telehealthNotes?.length || 0,
      hospitalDischargeNotes: hospitalDischargeNotes?.length || 0,
    });
  }, [followUpExamNotes, annualWellnessNotes, telehealthNotes, hospitalDischargeNotes]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 2 && !notesLoaded) {
      loadClinicalNotes();
    }
  };

  // Organize notes by type
  const notesByType = useMemo(() => ({
    followUpExam: followUpExamNotes || [],
    annualWellness: annualWellnessNotes || [],
    telehealth: telehealthNotes || [],
    hospitalDischarge: hospitalDischargeNotes || [],
  }), [followUpExamNotes, annualWellnessNotes, telehealthNotes, hospitalDischargeNotes]);

  const totalNotes = useMemo(() => {
    return NOTE_TYPES.reduce(
      (acc, type) => acc + (notesByType[type.key]?.length || 0),
      0
    );
  }, [notesByType]);

  const notesLoading =
    lookupLoading ||
    followUpExamLoading ||
    annualWellnessLoading ||
    telehealthLoading ||
    hospitalDischargeLoading;

  // Last visit data
  const lastVisit = useMemo(() => {
    const d = NoRecentVisitDetails;
    if (!d) return null;
    if (Array.isArray(d)) return d[0] ?? null;
    if (Array.isArray(d?.data)) return d.data[0] ?? null;
    return d;
  }, [NoRecentVisitDetails]);

  // Membership status helper (status: 0 = Active, 1 = Inactive based on common patterns)
  const getMembershipStatus = (m) => {
    if (m.status === 0 || m.status === "0" || m.status === "Active" || m.status === "ACTIVE") {
      return "Active";
    }
    if (m.status === 1 || m.status === "1" || m.status === "Inactive" || m.status === "INACTIVE") {
      return "Inactive";
    }
    return m.statusText || m.status || "Unknown";
  };

  // Membership active count
  const membershipActive = useMemo(() => {
    if (!Array.isArray(membership)) return 0;
    return membership.filter((m) => getMembershipStatus(m) === "Active").length;
  }, [membership]);

  // Check if care gap is closed
  const isClosed =
    item?.sourceStatusCode === "CLOSED" ||
    item?.workflowStatusCode === "VERIFIED" ||
    item?.workflowStatusCode === "CLOSED_BY_USER" ||
    item?.workflowStatusCode === "EXEMPT";

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: { xs: "100%", sm: 560 } } }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            bgcolor: isClosed ? "grey.100" : "primary.50",
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight={800}>
                {item?.careGapName || "Care Gap Detail"}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Patient: <strong>{patientName}</strong>
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                <Chip
                  label={item?.sourceStatusCode || "—"}
                  size="small"
                  color={item?.sourceStatusCode === "OPEN" ? "warning" : "default"}
                />
                <Chip label={`CIP ${cipId || "—"}`} size="small" variant="outlined" />
                <Chip label={`MY ${item?.measurementYear || "—"}`} size="small" variant="outlined" />
                {item?.historicallyNonCompliant && (
                  <Chip
                    icon={<WarningAmberIcon />}
                    label="Non-Compliant History"
                    size="small"
                    color="error"
                    variant="outlined"
                  />
                )}
              </Stack>
            </Box>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
            <Tab
              icon={<AssignmentIcon />}
              label="Care Gap"
              iconPosition="start"
              sx={{ minHeight: 48 }}
            />
            <Tab
              icon={<PersonOutlineIcon />}
              label="Patient"
              iconPosition="start"
              sx={{ minHeight: 48 }}
            />
            <Tab
              icon={<DescriptionIcon />}
              label="Clinical Notes"
              iconPosition="start"
              sx={{ minHeight: 48 }}
            />
          </Tabs>
        </Box>

        {/* Tab Content */}
        <Box sx={{ p: 2, overflowY: "auto", flex: 1 }}>
          {/* TAB 0: Care Gap Info */}
          <TabPanel value={activeTab} index={0}>
            <Stack spacing={2}>
              {/* Care Gap Details */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                  Care Gap Information
                </Typography>
                <Stack spacing={1}>
                  <InfoRow
                    icon={<AssignmentIcon fontSize="small" />}
                    label="Status"
                    value={item?.workflowStatusCode || item?.workflowStatus}
                  />
                  <InfoRow
                    icon={<MedicalServicesOutlinedIcon fontSize="small" />}
                    label="PCP"
                    value={item?.pcpName}
                  />
                  <InfoRow
                    icon={<EventOutlinedIcon fontSize="small" />}
                    label="Last Update"
                    value={formatDate(item?.lastUpdated)}
                  />
                  <InfoRow
                    icon={<EventOutlinedIcon fontSize="small" />}
                    label="Service Closure"
                    value={formatDate(item?.serviceClosureDate)}
                  />
                </Stack>
              </Paper>

              {/* Actions */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                  Actions
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button
                    variant="contained"
                    onClick={() => onSchedule(item)}
                    disabled={isClosed}
                    size="small"
                  >
                    Schedule
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => onSubmitEvidence(item)}
                    disabled={isClosed}
                    size="small"
                  >
                    Submit to AVALITY
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => onLogOutreach(item)}
                    disabled={isClosed}
                    size="small"
                  >
                    Log Outreach
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => onCareGapActions?.(item)}
                    disabled={isClosed}
                    size="small"
                  >
                    Care Gap Actions
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => onShowObservations(item)}
                    size="small"
                  >
                    Observations
                  </Button>
                </Stack>
                {isClosed && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    This care gap is closed. Actions are disabled.
                  </Alert>
                )}
              </Paper>
            </Stack>
          </TabPanel>

          {/* TAB 1: Patient Info */}
          <TabPanel value={activeTab} index={1}>
            <Stack spacing={2}>
              {/* Patient Info from API */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: "primary.50" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    <PersonOutlineIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: "middle" }} />
                    Patient Information
                  </Typography>
                  {patientInfoLoading && <CircularProgress size={16} />}
                  {patientInfo?.status && (
                    <Chip
                      size="small"
                      label={patientInfo.status}
                      color={patientInfo.status === "ACTIVE" ? "success" : "default"}
                    />
                  )}
                </Stack>

                {patientInfoError && (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    {patientInfoError}
                  </Alert>
                )}

                {patientInfo && (
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={4} flexWrap="wrap">
                      <Box>
                        <Typography variant="caption" color="text.secondary">Full Name</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {safeText(patientInfo.memberFullName || `${patientInfo.firstName} ${patientInfo.lastName}`)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Member ID</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {safeText(patientInfo.memberId)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">CIP ID</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {safeText(patientInfo.varCipId)}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={4} flexWrap="wrap">
                      <Box>
                        <Typography variant="caption" color="text.secondary">Date of Birth</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {formatDateShort(patientInfo.dateOfBirth)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Age</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {safeText(patientInfo.age)} years
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Gender</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {patientInfo.gender === "F" ? "Female" : patientInfo.gender === "M" ? "Male" : safeText(patientInfo.gender)}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={4} flexWrap="wrap">
                      <Box>
                        <Typography variant="caption" color="text.secondary">Email</Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ wordBreak: "break-all" }}>
                          {safeText(patientInfo.memberEmail)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Phone</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {safeText(patientInfo.memberPhone)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Mobile</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {safeText(patientInfo.memberMobile)}
                        </Typography>
                      </Box>
                    </Stack>
                    {/* Risk Indicators */}
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                      {patientInfo.numAdmittedId > 0 && (
                        <Chip size="small" label={`Admitted: ${patientInfo.numAdmittedId}`} color="warning" variant="outlined" />
                      )}
                      {patientInfo.dischargeId > 0 && (
                        <Chip size="small" label={`Discharge: ${patientInfo.dischargeId}`} color="info" variant="outlined" />
                      )}
                      {patientInfo.numEmergencyId > 0 && (
                        <Chip size="small" label={`Emergency: ${patientInfo.numEmergencyId}`} color="error" variant="outlined" />
                      )}
                      {patientInfo.numHighCostId > 0 && (
                        <Chip size="small" label={`High Cost: ${patientInfo.numHighCostId}`} color="warning" variant="outlined" />
                      )}
                      {patientInfo.numHighRiskId > 0 && (
                        <Chip size="small" label={`High Risk: ${patientInfo.numHighRiskId}`} color="error" variant="outlined" />
                      )}
                    </Stack>
                  </Stack>
                )}

                {!patientInfoLoading && !patientInfo && !patientInfoError && (
                  <Typography variant="body2" color="text.secondary">
                    No patient information available.
                  </Typography>
                )}
              </Paper>

              {/* AdvancedMD Match Section */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>
                      <LinkIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: "middle" }} />
                      AdvancedMD Match
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Patient enrichment & clinical notes source
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={confidence != null && candidate ? `${advStatus.label} · ${confidence}%` : advStatus.label}
                    color={advStatus.color}
                    variant={candidate ? "filled" : "outlined"}
                  />
                </Stack>

                {lookupLoading && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      Searching in AdvancedMD...
                    </Typography>
                  </Stack>
                )}

                {!lookupLoading && candidate && (
                  <Stack spacing={0.5}>
                    <Typography variant="body2">
                      <strong>Patient ID:</strong> {safeText(candidate.id)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Chart:</strong> {safeText(chartValue)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Name:</strong> {safeText(candidate.name || `${candidate.firstName || ""} ${candidate.lastName || ""}`.trim())}
                    </Typography>
                    <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                      <strong>Email:</strong> {safeText(candidate.email)}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Address:</strong> {shortAddress(candidate)}
                    </Typography>
                    {advancedMdPatientId && (
                      <Typography variant="body2" color="success.main">
                        <strong>Normalized ID:</strong> {advancedMdPatientId}
                      </Typography>
                    )}
                  </Stack>
                )}

                {!lookupLoading && !candidate && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      {lookupError
                        ? "AdvancedMD lookup failed."
                        : matchAttempted
                        ? "No AdvancedMD patient matched."
                        : "Click to search patient in AdvancedMD."}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={performAdvancedMdMatch}
                      disabled={lookupLoading || !patientName}
                    >
                      {matchAttempted ? "Retry" : "Search"}
                    </Button>
                  </Stack>
                )}
              </Paper>

              {/* Membership Status */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Memberships
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    {membershipActive > 0 && (
                      <Chip
                        icon={<CheckCircleIcon />}
                        label={`${membershipActive} Active`}
                        size="small"
                        color="success"
                      />
                    )}
                    <Chip
                      label={`${membership?.length || 0} Total`}
                      size="small"
                      variant="outlined"
                    />
                  </Stack>
                </Stack>

                {membershipLoading && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      Loading memberships...
                    </Typography>
                  </Stack>
                )}

                {membershipError && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    Error loading memberships
                  </Alert>
                )}

                {!membershipLoading && Array.isArray(membership) && membership.length > 0 && (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Plan / Network</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Effective</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {membership.slice(0, 5).map((m, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {safeText(m.memberPlan || m.planName || m.plan)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {safeText(m.networkName)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={safeText(m.memberType)}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            {formatDateShort(m.memberPlanEffectiveDate || m.effectiveDate)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getMembershipStatus(m)}
                              size="small"
                              color={getMembershipStatus(m) === "Active" ? "success" : "default"}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}

                {!membershipLoading && (!membership || membership.length === 0) && (
                  <Typography variant="body2" color="text.secondary">
                    No memberships found for this patient.
                  </Typography>
                )}
              </Paper>

              {/* Last Visit */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Last Visit
                  </Typography>
                  {lastVisitLoading && <CircularProgress size={16} />}
                </Stack>

                {lastVisitError && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    Error loading last visit
                  </Alert>
                )}

                {!lastVisitLoading && lastVisit && (
                  <Stack spacing={1}>
                    <InfoRow
                      icon={<EventOutlinedIcon fontSize="small" />}
                      label="Date"
                      value={formatDateShort(lastVisit.dateOfLastVisit)}
                    />
                    <InfoRow
                      icon={<AccessTimeOutlinedIcon fontSize="small" />}
                      label="Time Since"
                      value={lastVisit.timeSinceLastVisit || "—"}
                    />
                    <InfoRow
                      icon={<PlaceOutlinedIcon fontSize="small" />}
                      label="Facility"
                      value={
                        lastVisit.facilityName ||
                        lastVisit.placeOfServiceDescription ||
                        lastVisit.medicalCenter
                      }
                    />
                    <InfoRow
                      icon={<MedicalServicesOutlinedIcon fontSize="small" />}
                      label="Provider"
                      value={
                        lastVisit.attendingPhysicianProvider ||
                        lastVisit.pcpAssigned
                      }
                    />
                    <InfoRow
                      icon={<BadgeOutlinedIcon fontSize="small" />}
                      label="Status"
                      value={lastVisit.insureStatus || lastVisit.status}
                    />
                  </Stack>
                )}

                {!lastVisitLoading && !lastVisit && (
                  <Typography variant="body2" color="text.secondary">
                    No last visit record found for this patient.
                  </Typography>
                )}
              </Paper>

              {/* Patient Assignment */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                  Assignment
                </Typography>
                <Stack spacing={1}>
                  <InfoRow
                    icon={<MedicalServicesOutlinedIcon fontSize="small" />}
                    label="PCP"
                    value={item?.pcpName}
                  />
                  <InfoRow
                    icon={<BadgeOutlinedIcon fontSize="small" />}
                    label="CIP ID"
                    value={cipId}
                  />
                </Stack>
              </Paper>
            </Stack>
          </TabPanel>

          {/* TAB 2: Clinical Notes */}
          <TabPanel value={activeTab} index={2}>
            <Stack spacing={2}>
              {/* AdvancedMD Status for Notes */}
              <Paper sx={{ p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Clinical Notes
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Notes from AdvancedMD (ID: {advancedMdPatientId || "not matched"})
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={advStatus.label}
                      size="small"
                      color={advStatus.color}
                      variant="outlined"
                    />
                    <Chip
                      label={`${totalNotes} notes`}
                      variant="outlined"
                      size="small"
                    />
                  </Stack>
                </Stack>

                {!candidate && !lookupLoading && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Typography variant="body2">
                        Patient not matched in AdvancedMD. Clinical notes unavailable.
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={performAdvancedMdMatch}
                      >
                        Retry Match
                      </Button>
                    </Stack>
                  </Alert>
                )}
              </Paper>

              {/* Loading state */}
              {notesLoading && (
                <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 2 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Loading clinical notes...
                  </Typography>
                </Stack>
              )}

              {/* Error state */}
              {lookupError && (
                <Alert severity="error" icon={<ErrorOutlineIcon />}>
                  Error connecting to AdvancedMD. Please try again.
                </Alert>
              )}

              {/* No notes */}
              {!notesLoading && candidate && totalNotes === 0 && (
                <Alert severity="info">
                  No clinical notes found for this patient in AdvancedMD.
                </Alert>
              )}

              {/* Notes by type */}
              {!notesLoading &&
                candidate &&
                NOTE_TYPES.map((type) => {
                  const notes = notesByType[type.key] || [];
                  if (notes.length === 0) return null;

                  return (
                    <Accordion key={type.key} defaultExpanded={notes.length <= 3}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <DescriptionIcon color={type.color} />
                          <Typography fontWeight={600}>{type.label}</Typography>
                          <Chip
                            size="small"
                            label={`${notes.length}`}
                            color={type.color}
                            variant="outlined"
                          />
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Date</TableCell>
                              <TableCell>Provider</TableCell>
                              <TableCell align="right">Action</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {notes.map((note, idx) => {
                              const noteId = note.noteId || note.id || `note_${idx}`;
                              return (
                                <TableRow key={noteId} hover>
                                  <TableCell>
                                    {formatDateShort(
                                      note.noteDateTime || note.noteDate || note.date
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {note.userName || note.providerName || "—"}
                                  </TableCell>
                                  <TableCell align="right">
                                    <Tooltip title="View Note">
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          setSelectedNote({ ...note, __group: type.label })
                                        }
                                      >
                                        <VisibilityIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
            </Stack>
          </TabPanel>
        </Box>
      </Drawer>

      {/* Dialog: View Clinical Note */}
      <Dialog
        open={!!selectedNote}
        onClose={() => setSelectedNote(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          Clinical Note - {selectedNote?.__group || "Note"}
        </DialogTitle>
        <DialogContent dividers>
          {selectedNote && (
            <Box>
              <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
                <Chip
                  size="small"
                  label={`Date: ${formatDateShort(
                    selectedNote.noteDateTime || selectedNote.noteDate
                  )}`}
                  variant="outlined"
                />
                {selectedNote.userName && (
                  <Chip
                    size="small"
                    label={`Provider: ${selectedNote.userName}`}
                    variant="outlined"
                  />
                )}
                {selectedNote.templateName && (
                  <Chip
                    size="small"
                    label={`Template: ${selectedNote.templateName}`}
                    variant="outlined"
                  />
                )}
                {selectedNote.signedByUser && (
                  <Chip
                    size="small"
                    label={`Signed: ${selectedNote.signedByUser}`}
                    variant="outlined"
                  />
                )}
              </Stack>

              <Paper
                variant="outlined"
                sx={{ p: 2, maxHeight: 500, overflow: "auto", borderRadius: 2 }}
              >
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}
                >
                  {flattenNoteToText(selectedNote)}
                </Typography>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedNote(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
